import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkUpdateTicketsOptions,
  BulkUpdateTicketsResponse,
  CheckInTicketResponse,
  CheckInTicketsOptions,
  DeleteTicketCheckInResponse,
  DeleteTicketCheckInsOptions,
  GetTicketOptions,
  ListTicketsOptions,
  ListTicketsResponse,
  TicketOrderUpdatedEnvelope,
  TicketingTicket,
  UpdateTicketOptions,
  UpdateTicketResponse,
  bulkUpdateTickets as universalBulkUpdateTickets,
  checkInTickets as universalCheckInTickets,
  deleteTicketCheckIns as universalDeleteTicketCheckIns,
  getTicket as universalGetTicket,
  listTickets as universalListTickets,
  updateTicket as universalUpdateTicket,
} from './events-v1-ticket-tickets.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/events' };

export function listTickets(httpClient: HttpClient): ListTicketsSignature {
  return (options?: ListTicketsOptions) =>
    universalListTickets(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListTicketsSignature {
  /**
   * Retrieves a list of up to 100 tickets.
   *
   * <!--
   * >**Note:** This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
   * -->
   * @param - Options for defining the returned list of tickets.
   */
  (options?: ListTicketsOptions): Promise<
    NonNullablePaths<
      ListTicketsResponse,
      | `total`
      | `offset`
      | `limit`
      | `tickets`
      | `tickets.${number}.ticketNumber`
      | `tickets.${number}.orderNumber`
      | `tickets.${number}.ticketDefinitionId`
      | `tickets.${number}.name`
      | `tickets.${number}.price.amount`
      | `tickets.${number}.price.currency`
      | `tickets.${number}.free`
      | `tickets.${number}.policy`
      | `tickets.${number}.orderStatus`
      | `tickets.${number}.orderArchived`
      | `tickets.${number}.orderFullName`
      | `tickets.${number}.guestDetails.guestAssigned`
      | `tickets.${number}.archived`
      | `tickets.${number}.anonymized`
      | `tickets.${number}.checkInUrl`
      | `tickets.${number}.ticketPdfUrl`
      | `tickets.${number}.channel`
      | `tickets.${number}.walletPassUrl`,
      5
    >
  >;
}

export function getTicket(httpClient: HttpClient): GetTicketSignature {
  return (ticketNumber: string, options?: GetTicketOptions) =>
    universalGetTicket(
      ticketNumber,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetTicketSignature {
  /**
   * Retrieves a ticket by the unique ticket number.
   * @param - A unique ticket number which is assigned automatically when creating a ticket.
   * @param - Details for the ticket to retrieve.
   * @param - Options for the returned ticket data.
   * @returns Ticket.
   */
  (ticketNumber: string, options?: GetTicketOptions): Promise<
    NonNullablePaths<
      TicketingTicket,
      | `ticketNumber`
      | `orderNumber`
      | `ticketDefinitionId`
      | `name`
      | `price.amount`
      | `price.currency`
      | `free`
      | `policy`
      | `orderStatus`
      | `orderArchived`
      | `orderFullName`
      | `guestDetails.guestAssigned`
      | `guestDetails.form.inputValues`
      | `guestDetails.form.inputValues.${number}.inputName`
      | `guestDetails.form.inputValues.${number}.value`
      | `archived`
      | `anonymized`
      | `checkInUrl`
      | `ticketPdfUrl`
      | `channel`
      | `walletPassUrl`,
      6
    >
  >;
}

export function checkInTickets(
  httpClient: HttpClient
): CheckInTicketsSignature {
  return (eventId: string, options?: CheckInTicketsOptions) =>
    universalCheckInTickets(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CheckInTicketsSignature {
  /**
   * Checks in 1 or more tickets.
   * @param - Event ID to which the ticket belongs.
   * @param - Options for tickets to check-in.
   */
  (eventId: string, options?: CheckInTicketsOptions): Promise<
    NonNullablePaths<
      CheckInTicketResponse,
      | `tickets`
      | `tickets.${number}.ticketNumber`
      | `tickets.${number}.orderNumber`
      | `tickets.${number}.ticketDefinitionId`
      | `tickets.${number}.name`
      | `tickets.${number}.price.amount`
      | `tickets.${number}.price.currency`
      | `tickets.${number}.free`
      | `tickets.${number}.policy`
      | `tickets.${number}.orderStatus`
      | `tickets.${number}.orderArchived`
      | `tickets.${number}.orderFullName`
      | `tickets.${number}.guestDetails.guestAssigned`
      | `tickets.${number}.archived`
      | `tickets.${number}.anonymized`
      | `tickets.${number}.checkInUrl`
      | `tickets.${number}.ticketPdfUrl`
      | `tickets.${number}.channel`
      | `tickets.${number}.walletPassUrl`,
      5
    >
  >;
}

export function deleteTicketCheckIns(
  httpClient: HttpClient
): DeleteTicketCheckInsSignature {
  return (eventId: string, options?: DeleteTicketCheckInsOptions) =>
    universalDeleteTicketCheckIns(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteTicketCheckInsSignature {
  /**
   * Deletes check-ins for 1 or more tickets.
   * @param - Event ID to which the ticket belongs.
   * @param - Options for tickets to delete.
   */
  (eventId: string, options?: DeleteTicketCheckInsOptions): Promise<
    NonNullablePaths<
      DeleteTicketCheckInResponse,
      | `tickets`
      | `tickets.${number}.ticketNumber`
      | `tickets.${number}.orderNumber`
      | `tickets.${number}.ticketDefinitionId`
      | `tickets.${number}.name`
      | `tickets.${number}.price.amount`
      | `tickets.${number}.price.currency`
      | `tickets.${number}.free`
      | `tickets.${number}.policy`
      | `tickets.${number}.orderStatus`
      | `tickets.${number}.orderArchived`
      | `tickets.${number}.orderFullName`
      | `tickets.${number}.guestDetails.guestAssigned`
      | `tickets.${number}.archived`
      | `tickets.${number}.anonymized`
      | `tickets.${number}.checkInUrl`
      | `tickets.${number}.ticketPdfUrl`
      | `tickets.${number}.channel`
      | `tickets.${number}.walletPassUrl`,
      5
    >
  >;
}

export function updateTicket(httpClient: HttpClient): UpdateTicketSignature {
  return (ticketNumber: string, options?: UpdateTicketOptions) =>
    universalUpdateTicket(
      ticketNumber,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateTicketSignature {
  /**
   * Updates a ticket.
   * @param - A unique ticket number which is assigned automatically when creating a ticket.
   * @param - Details for the ticket to update.
   * @param - Ticket details to update.
   */
  (ticketNumber: string, options?: UpdateTicketOptions): Promise<
    NonNullablePaths<
      UpdateTicketResponse,
      | `ticket.ticketNumber`
      | `ticket.orderNumber`
      | `ticket.ticketDefinitionId`
      | `ticket.name`
      | `ticket.price.amount`
      | `ticket.price.currency`
      | `ticket.free`
      | `ticket.policy`
      | `ticket.orderStatus`
      | `ticket.orderArchived`
      | `ticket.orderFullName`
      | `ticket.guestDetails.guestAssigned`
      | `ticket.guestDetails.form.inputValues`
      | `ticket.guestDetails.form.inputValues.${number}.inputName`
      | `ticket.guestDetails.form.inputValues.${number}.value`
      | `ticket.archived`
      | `ticket.anonymized`
      | `ticket.checkInUrl`
      | `ticket.ticketPdfUrl`
      | `ticket.channel`
      | `ticket.walletPassUrl`,
      7
    >
  >;
}

export function bulkUpdateTickets(
  httpClient: HttpClient
): BulkUpdateTicketsSignature {
  return (options?: BulkUpdateTicketsOptions) =>
    universalBulkUpdateTickets(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateTicketsSignature {
  /**
   * Archives multiple tickets.
   * @param - Options for updating the tickets.
   * @param - Event ID to which the ticket belongs.
   */
  (options?: BulkUpdateTicketsOptions): Promise<
    NonNullablePaths<
      BulkUpdateTicketsResponse,
      | `tickets`
      | `tickets.${number}.ticketNumber`
      | `tickets.${number}.orderNumber`
      | `tickets.${number}.ticketDefinitionId`
      | `tickets.${number}.name`
      | `tickets.${number}.price.amount`
      | `tickets.${number}.price.currency`
      | `tickets.${number}.free`
      | `tickets.${number}.policy`
      | `tickets.${number}.orderStatus`
      | `tickets.${number}.orderArchived`
      | `tickets.${number}.orderFullName`
      | `tickets.${number}.guestDetails.guestAssigned`
      | `tickets.${number}.archived`
      | `tickets.${number}.anonymized`
      | `tickets.${number}.checkInUrl`
      | `tickets.${number}.ticketPdfUrl`
      | `tickets.${number}.channel`
      | `tickets.${number}.walletPassUrl`,
      5
    >
  >;
}

export const onTicketOrderUpdated = EventDefinition(
  'wix.events.ticketing.events.OrderUpdated',
  false,
  (event: TicketOrderUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.timestamp' },
            { path: 'data.created' },
            { path: 'data.updated' },
            { path: 'data.checkoutForm.inputValues.dateTime' },
            { path: 'data.tickets.checkIn.created' },
            { path: 'data.tickets.checkIn.checkInCanceledDate' },
            { path: 'data.tickets.form.inputValues.dateTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.checkoutForm.inputValues.number' },
            { path: 'data.tickets.form.inputValues.number' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.checkoutForm.inputValues.address.address' },
            { path: 'data.tickets.form.inputValues.address.address' },
          ],
        },
      ])
    )
)<TicketOrderUpdatedEnvelope>();

export {
  AccountInfo,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  BaseEventMetadata,
  BulkUpdateTicketsOptions,
  BulkUpdateTicketsRequest,
  BulkUpdateTicketsResponse,
  ChannelType,
  CheckIn,
  CheckInTicketRequest,
  CheckInTicketResponse,
  CheckInTicketsOptions,
  Counts,
  DeleteTicketCheckInRequest,
  DeleteTicketCheckInResponse,
  DeleteTicketCheckInsOptions,
  FacetCounts,
  FormResponse,
  FormSubmission,
  FormattedAddress,
  GetDemoTicketRequest,
  GetDemoTicketResponse,
  GetTicketOptions,
  GetTicketRequest,
  GetTicketResponse,
  GuestDetails,
  GuestDetailsUpdate,
  IdentificationData,
  IdentificationDataIdOneOf,
  InputValue,
  ListTicketsOptions,
  ListTicketsRequest,
  ListTicketsResponse,
  MessageEnvelope,
  Money,
  OnlineConferencingLogin,
  OrderStatus,
  OrderUpdated,
  StandardDetails,
  State,
  StreetAddress,
  Subdivision,
  SubdivisionType,
  Ticket,
  TicketDetails,
  TicketFacetCounts,
  TicketFacets,
  TicketFieldset,
  TicketOrderUpdatedEnvelope,
  TicketingTicket,
  UpdateTicketOptions,
  UpdateTicketRequest,
  UpdateTicketResponse,
  WebhookIdentityType,
} from './events-v1-ticket-tickets.universal.js';
